import { AssetIssues } from 'types/room';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { groupBy } from 'lodash';
import { Box, Button, Stack, Typography, SelectChangeEvent } from '@mui/material';
import { IconCollapse } from 'app/components/elements/icons/IconCollapse';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ContentExtraTableValidationSchema } from 'utils/validate';
import { InputText } from 'app/components/elements/InputText';
import { AssetTypes } from '../../type';
import { getAssetTypeDetail } from 'services/asset';
import { InputSelect } from 'app/components/elements/InputSelect';
import { DropdownOption } from 'app/pages/RoomReportViewPhotoPage/hook';

type Field = {
  fieldId: number;
  value?: string;
  dropdownValueId?: string;
  category?: string;
  dropdownOtherValue?: string;
  deleteDropdownId?: number;
};

export type FormEditSubmittedEntries = {
  fields?: Field[];
};

type ContentExtraTableValidationProps = {
  dataRowActive?: AssetIssues;
  onEditSubmittedEntries?: (value: FormEditSubmittedEntries) => void;
  mapOptions: Map<string, DropdownOption>;
  saveActionsOnTop?: boolean;
  fieldsGridLayout?: boolean;
};

const PREFERRED_CATEGORY = 'Manufacturers_and_Properties';

const buildOtherValueMap = (
  assetEntries: AssetTypes['assetEntries'] | undefined,
  submittedEntries: AssetIssues['submittedEntries'] | undefined,
  mapOptions: Map<string, DropdownOption>
): Record<number, boolean> => {
  const entriesMap = new Map<string, NonNullable<AssetIssues['submittedEntries']>[number]>();
  submittedEntries?.forEach((entry) => {
    entriesMap.set(entry.fieldId.toString(), entry);
  });

  const result: Record<number, boolean> = {};
  assetEntries?.forEach((entry) => {
    const fieldId = Number(entry.fieldId);
    const submitted = entriesMap.get(entry.fieldId.toString());
    if (typeof submitted?.dropdownOtherValue === 'string' && submitted.dropdownOtherValue !== '') {
      result[fieldId] = true;
      return;
    }
    const dropdownValues = mapOptions.get(
      entry?.field?.dropdownValueOptionId?.toString()
    )?.dropdownValues;
    const selected = dropdownValues?.find(
      (drop) => drop.id.toString() === submitted?.dropdownValueId?.toString()
    );
    result[fieldId] = selected?.value === 'OTHER';
  });
  return result;
};

export const ContentExtraTableValidation = ({
  dataRowActive,
  onEditSubmittedEntries,
  mapOptions,
  saveActionsOnTop = false,
  fieldsGridLayout = false
}: ContentExtraTableValidationProps) => {
  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    formState: { errors }
  } = useForm<FormEditSubmittedEntries>({
    resolver: yupResolver(ContentExtraTableValidationSchema)
  });
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [dataActiveAssetType, setDataActiveAssetType] = useState<AssetTypes>();
  const [otherValueByFieldId, setOtherValueByFieldId] = useState<Record<number, boolean>>({});

  useLayoutEffect(() => {
    if (dataRowActive?.type?.id) {
      const f = async () => {
        const result = await getAssetTypeDetail(dataRowActive?.type?.id);
        setDataActiveAssetType(result.type);
      };

      f();
    }
  }, [dataRowActive?.type?.id]);

  const groupedData = groupBy(dataActiveAssetType?.assetEntries, (item) => item.field.category);
  const categories = Object.keys(groupedData);

  useEffect(() => {
    if (dataActiveAssetType?.assetEntries.length) {
      const entriesMap = new Map();
      dataRowActive?.submittedEntries?.forEach((entry) => {
        entriesMap.set(entry.fieldId.toString(), entry);
      });

      reset({
        fields: dataActiveAssetType?.assetEntries.map((item) => ({
          value: entriesMap.get(item.fieldId.toString())?.value,
          dropdownValueId: entriesMap.get(item.fieldId.toString())
            ? entriesMap.get(item.fieldId.toString())?.dropdownValueId
            : undefined,
          dropdownOtherValue: entriesMap.get(item.fieldId.toString())?.dropdownOtherValue,
          fieldId: Number(item.fieldId),
          name: item.field.name,
          unit: item.field.unit,
          category: item.field.category
        }))
      });
      setOtherValueByFieldId(
        buildOtherValueMap(
          dataActiveAssetType.assetEntries,
          dataRowActive?.submittedEntries,
          mapOptions
        )
      );

      const categoryKeys = [
        ...new Set(dataActiveAssetType.assetEntries.map((item) => item.field.category))
      ];
      const defaultCategory = categoryKeys.includes(PREFERRED_CATEGORY)
        ? PREFERRED_CATEGORY
        : (categoryKeys[0] ?? null);
      setSelectedCategory(defaultCategory);
      entriesMap.clear();
    }
    return () => {
      setSelectedCategory(null);
      setOtherValueByFieldId({});
    };
  }, [dataActiveAssetType, dataRowActive, reset, mapOptions]);

  const fieldIdToFormIndex = useMemo(() => {
    const mapping: Record<number, number> = {};
    dataActiveAssetType?.assetEntries?.forEach((entry, index) => {
      mapping[Number(entry.fieldId)] = index;
    });

    return mapping;
  }, [dataActiveAssetType?.assetEntries]);

  const saveButton =
    !dataRowActive?.archivedAt && onEditSubmittedEntries ? (
      <ButtonCustom
        style={{ width: '120px', height: '40px', marginTop: saveActionsOnTop ? 0 : '20px' }}
        label={'Save'}
        onClick={handleSubmit((data) => {
          const transformedData = {
            ...data,
            fields:
              data.fields?.map((field) => ({
                fieldId: field.fieldId,
                value: field.value || '',
                dropdownValueId: field.dropdownValueId,
                dropdownOtherValue: field?.dropdownOtherValue
              })) || []
          };

          onEditSubmittedEntries(transformedData);
        })}
      />
    ) : null;

  return (
    <Stack minHeight={500}>
      {saveActionsOnTop && saveButton && (
        <Stack mb={2} flexDirection="row">
          {saveButton}
        </Stack>
      )}
      <Stack flexDirection="row" gap={8}>
        <Stack>
          {categories.map((category, index) => (
            <Button
              key={index}
              onClick={() => {
                setSelectedCategory(selectedCategory === category ? null : category);
              }}
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '5px',
                alignItems: 'center',
                justifyContent: 'flex-start'
              }}>
              <Typography
                fontSize="14px"
                fontWeight={selectedCategory === category ? 700 : 400}
                color={selectedCategory === category ? '#226AAA' : '#5F5F5F'}>
                {category.replace('_', ' ')}
              </Typography>
              <IconCollapse
                color={selectedCategory === category ? '#226AAA' : '#5F5F5F'}
                direction={selectedCategory === category ? 'down' : 'right'}
              />
            </Button>
          ))}
        </Stack>
        <Stack maxWidth={1200} flex={1} minWidth={0}>
          {selectedCategory && (
            <Box
              sx={
                fieldsGridLayout
                  ? {
                      mt: 1,
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                      gap: 2,
                      '& > *': { minWidth: 0 }
                    }
                  : {
                      mt: 1,
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: 4
                    }
              }>
              {dataActiveAssetType?.assetEntries?.map((entry, index) => {
                if (entry.field.category !== selectedCategory) return;
                const fieldId = Number(entry.fieldId);
                const isOtherValue = !!otherValueByFieldId[fieldId];

                return (
                  <Stack
                    gap={1}
                    mr={fieldsGridLayout ? 0 : 4}
                    key={index}
                    width={fieldsGridLayout ? '100%' : '250px'}>
                    <Typography style={{ color: '#999999', fontSize: '12px', fontWeight: '700' }}>
                      {entry.field.name}
                    </Typography>
                    <Stack
                      flexDirection={'row'}
                      alignItems={'center'}
                      justifyContent={'flex-start'}
                      gap={0.5}>
                      {mapOptions.get(entry?.field?.dropdownValueOptionId?.toString()) ? (
                        <>
                          <Controller
                            control={control}
                            name={`fields.${fieldIdToFormIndex[fieldId]}.dropdownValueId`}
                            render={({ field: { onChange, value } }) => (
                              <InputSelect
                                value={value ? value.toString() : ''}
                                placeholder="Select Value"
                                onChange={(e: SelectChangeEvent<unknown>) => {
                                  const selectedValue = e.target.value;
                                  onChange(selectedValue);
                                  setValue(`fields.${fieldIdToFormIndex[fieldId]}.value`, '');
                                  const dropdownValues = mapOptions.get(
                                    entry?.field?.dropdownValueOptionId?.toString()
                                  )?.dropdownValues;
                                  const activeDropdown = dropdownValues?.find(
                                    (drop) => drop.id.toString() === selectedValue
                                  );
                                  setOtherValueByFieldId((prev) => ({
                                    ...prev,
                                    [fieldId]: activeDropdown?.value === 'OTHER'
                                  }));
                                }}
                                options={mapOptions
                                  .get(entry?.field?.dropdownValueOptionId?.toString())
                                  ?.dropdownValues.map((dropdownValue) => ({
                                    value: dropdownValue.id.toString(),
                                    label: dropdownValue.value
                                  }))}
                                sx={{ minHeight: '46px', height: '46px', width: '170' }}
                              />
                            )}
                          />
                          {isOtherValue && (
                            <InputText
                              colorTitle={'#226AAA'}
                              inputError={
                                errors.fields?.[fieldIdToFormIndex[fieldId]]?.value?.message
                              }
                              placeholder={'Value'}
                              inputProps={{
                                ...register(
                                  `fields.${fieldIdToFormIndex[fieldId]}.dropdownOtherValue`
                                ),
                                disabled: !!dataRowActive?.archivedAt,
                                style: { minHeight: '46px', height: '46px', width: 170 }
                              }}
                            />
                          )}
                        </>
                      ) : (
                        <InputText
                          colorTitle={'#226AAA'}
                          inputError={errors.fields?.[fieldIdToFormIndex[fieldId]]?.value?.message}
                          placeholder={'Value'}
                          inputProps={{
                            ...register(`fields.${fieldIdToFormIndex[fieldId]}.value`),
                            disabled: !!dataRowActive?.archivedAt,
                            style: { minHeight: '46px', height: '46px', width: 170 }
                          }}
                        />
                      )}
                      <Typography
                        style={{
                          width: '100px',
                          color: '#000',
                          fontSize: '14px',
                          fontWeight: '400'
                        }}>
                        {entry.field.unit}
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })}
            </Box>
          )}
        </Stack>
      </Stack>
      {!saveActionsOnTop && saveButton}
    </Stack>
  );
};
